import { Fragment, useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Input, Row } from "reactstrap";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import { ensureDexieReady, getFilteredPositionIds } from "../../db/DexieServises";
import { RootState } from "../../redux-toolkit/store";
import { dynamicImage } from "../../Utils";
import { Image } from "../../AbstractElements";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import FeatherIconCom from "../../Utils/CommonComponents/CommonIcons/FeatherIconCom";

import MonthDropdown from "../CommonCompo/MonthDropdown";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import CommonTableNew from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";

const labelFromKey = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const cellToString = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export default function SupportCaptureSummary() {
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [displayedData, setDisplayedData] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const serverUrl = localStorage.getItem("serverUrl");
  const APP_TOKEN = localStorage.getItem("appToken");

  useEffect(() => {
    const fetchPositionIds = async () => {
      if (!unitId) return;
      const ids = await getFilteredPositionIds("Position", unitId);
      setSelectedIds(ids);
    };
    fetchPositionIds();
  }, [unitId]);

  useEffect(() => {
    if (selectedMonth && unitId) fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedIds, unitId]);

  const fetchSummary = async () => {
    if (!serverUrl || !APP_TOKEN || !unitId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    await ensureDexieReady();
    const ids = await getFilteredPositionIds("Position", unitId);
    const positionIds = selectedIds.length > 0 ? selectedIds : ids;

    const url = `${serverUrl}${API.getSupportCaptureSummary}`;
    const { status, data } = await getData(url, true, {
      position_id: positionIds,
      moye: selectedMonth,
    });

    if (status === 200 && Array.isArray(data?.data)) {
      setRawData(data.data);
      setDisplayedData(data.data);
    } else {
      setRawData([]);
      setDisplayedData([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    const lower = searchText.trim().toLowerCase();
    if (!lower) {
      setDisplayedData(rawData);
      return;
    }

    const filtered = rawData.filter((item) =>
      Object.values(item || {}).some((val) =>
        cellToString(val).toLowerCase().includes(lower),
      ),
    );
    setDisplayedData(filtered);
  }, [searchText, rawData]);

  const columnKeys = useMemo(() => {
    const keys = new Set<string>();
    displayedData.slice(0, 50).forEach((row) => {
      Object.keys(row || {}).forEach((k) => keys.add(k));
    });
    return Array.from(keys);
  }, [displayedData]);

  const headData = useMemo(
    () =>
      columnKeys.map((key, index) => ({
        id: String(index + 1),
        head: labelFromKey(key),
        key,
        sortKey: key,
      })),
    [columnKeys],
  );

  const handleExportToExcel = () => {
    if (displayedData.length === 0) return;

    const exportData = displayedData.map((row) => {
      const out: Record<string, any> = {};
      columnKeys.forEach((k) => {
        out[labelFromKey(k)] = cellToString((row as any)?.[k]);
      });
      return out;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Support Capture Summary");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Support_Capture_Summary_${selectedMonth || "Report"}.xlsx`);
  };

  return (
    <div className="page-body p-2">
      <Card>
        <div className="d-flex justify-content-between align-items-center pt-2 pb-2">
          <CardHeaderSpan
            headingClassName="pb-0"
            back={true}
            heading="Support Capture Summary"
          />
          <div className="p-3">
            <MonthDropdown onChange={(value) => setSelectedMonth(value)} />
          </div>
        </div>

        <div className="row px-3 mb-3 align-items-center justify-content-between">
          <div className="col-md-8 d-flex align-items-center gap-3 flex-wrap">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
              className="form-control"
              style={{ maxWidth: "250px" }}
            />
            <TreeModel onSelectionChange={setSelectedIds} />
          </div>

          <div className="col-md-4 d-flex justify-content-end gap-3">
            <button
              className="btn btn-sm btn-primary align-items-center d-flex"
              onClick={handleExportToExcel}
              disabled={displayedData.length === 0}
            >
              <FeatherIconCom iconName={"Download"} size={14} className="text-light" />
              <span className="px-2" style={{ fontSize: 14 }}>
                Export
              </span>
            </button>
          </div>
        </div>

        <Fragment>
          <Container fluid>
            <Row>
              <Col sm={12}>
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-3">
                      <div>
                        <Image
                          className="b-r-10 img-80"
                          src={dynamicImage("dashboard/loader.gif")}
                          alt="Loading..."
                        />
                      </div>
                    </div>
                  ) : (
                    <CommonTableNew
                      caption=""
                      headData={headData}
                      data={displayedData}
                      renderRow={(row, rowIndex) => (
                        <tr key={rowIndex}>
                          {columnKeys.map((k) => (
                            <td key={k}>{cellToString((row as any)?.[k])}</td>
                          ))}
                        </tr>
                      )}
                    />
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </Fragment>
      </Card>
    </div>
  );
}
